// ===============================================================
// 🎁 GiftStorePage.js — BOTTOM SLIDER ONLY
// - حذف التبويبات العلوية بالكامل
// - الإرسال فقط من الزر
// - السلايدر السفلي فقط
// - النقاط السفلية للتنقل
// ===============================================================

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { GIFT_TABS, getEnabledGiftsByTab } from "./giftsCatalog";
import "./GiftStorePage.css";

export default function GiftStorePage({
  open,
  onClose,
  onSendGift,
  sendingGiftId = "",
  viewerRole = null,
  defaultTab = "small",
  title = "",
}) {
  const { t } = useTranslation();
  // Fail-closed: only a normalized "male" role may send. Any other value -
  // "female", null, undefined, or an unrecognized string - renders view-only.
  const canSendGifts = viewerRole === "male";
  const viewOnly = !canSendGifts;
  const viewOnlyLabel = t("chat.gifts_males_only", {
    defaultValue: "Gifts are available for male accounts only.",
  });
  const sliderRef = useRef(null);
  const programmaticScrollRef = useRef(false);
  const scrollUnlockTimerRef = useRef(null);

  const tabsData = GIFT_TABS;

  const getIndexByKey = (key) => {
    const idx = tabsData.findIndex((tab) => tab.key === key);
    return idx >= 0 ? idx : 0;
  };

  const [activeIndex, setActiveIndex] = useState(getIndexByKey(defaultTab));

  useEffect(() => {
    return () => {
      if (scrollUnlockTimerRef.current) {
        clearTimeout(scrollUnlockTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const startIndex = getIndexByKey(defaultTab || "small");
    setActiveIndex(startIndex);

    requestAnimationFrame(() => {
      if (!sliderRef.current) return;

      const el = sliderRef.current;
      const width = el.clientWidth || 1;

      el.scrollTo({
        left: startIndex * width,
        behavior: "auto",
      });
    });
  }, [open, defaultTab]);

  const goToIndex = (index) => {
    const safeIndex = Math.max(0, Math.min(index, tabsData.length - 1));
    setActiveIndex(safeIndex);

    if (!sliderRef.current) return;

    const el = sliderRef.current;
    const width = el.clientWidth || 1;

    programmaticScrollRef.current = true;

    if (scrollUnlockTimerRef.current) {
      clearTimeout(scrollUnlockTimerRef.current);
    }

    el.scrollTo({
      left: safeIndex * width,
      behavior: "smooth",
    });

    scrollUnlockTimerRef.current = setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 350);
  };

  const handleScroll = () => {
    if (!sliderRef.current) return;
    if (programmaticScrollRef.current) return;

    const el = sliderRef.current;
    const width = el.clientWidth || 1;
    const nextIndex = Math.floor((el.scrollLeft + width / 2) / width);
    const safeIndex = Math.max(0, Math.min(nextIndex, tabsData.length - 1));

    if (safeIndex !== activeIndex) {
      setActiveIndex(safeIndex);
    }
  };

  if (!open) return null;

  return (
    <div
      className="giftstore-sheet giftstore-sheet-light"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="giftstore-header">
        <div className="giftstore-header-text">
          <h3 className="giftstore-title">{title || t("gifts.store.title", { defaultValue: "Gifts" })}</h3>
          <p className="giftstore-subtitle">
            {t("gifts.store.subtitle", { defaultValue: "Swipe between sections, then tap send." })}
          </p>
        </div>

        <button
          type="button"
          className="giftstore-close-btn"
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </div>

      <div
        ref={sliderRef}
        className="giftstore-slider"
        onScroll={handleScroll}
      >
        {tabsData.map((tab) => {
          const gifts = getEnabledGiftsByTab(tab.key);

          return (
            <div key={tab.key} className="giftstore-slide">
              <div className="giftstore-content">
                {gifts.length === 0 ? (
                  <div className="giftstore-empty">
                    {t("gifts.store.empty_section", { defaultValue: "No gifts in this section right now." })}
                  </div>
                ) : (
                  <div className="giftstore-grid">
                    {gifts.map((gift) => {
                      const isSending = sendingGiftId === gift.id;

                      return (
                        <div
                          key={gift.id}
                          className={`giftstore-card ${isSending ? "sending" : ""}`}
                        >
                          <div className="giftstore-card-icon">
                            {gift.previewUrl ? (
                              <img
                                src={gift.previewUrl}
                                alt={t(`gifts.items.${gift.id}`, { defaultValue: gift.name || "Gift" })}
                                className="giftstore-card-preview"
                                draggable="false"
                              />
                            ) : (
                              <span className="giftstore-card-emoji">
                                {gift.icon || "🎁"}
                              </span>
                            )}
                          </div>

                          <div className="giftstore-card-name">{t(`gifts.items.${gift.id}`, { defaultValue: gift.name })}</div>

                          <button
                            type="button"
                            className={`giftstore-send-btn${viewOnly ? " giftstore-send-btn--view-only" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Belt-and-suspenders: even if disabled were
                              // ever bypassed, this guard still blocks the
                              // callback from firing for a non-male viewer.
                              if (viewOnly) return;
                              onSendGift?.(gift);
                            }}
                            disabled={viewOnly || Boolean(sendingGiftId)}
                          >
                            {viewOnly
                              ? viewOnlyLabel
                              : isSending
                              ? t("gifts.store.sending", { defaultValue: "Sending..." })
                              : t("gifts.store.send_price", { defaultValue: "Send {{price}} Coins", price: gift.price })}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="giftstore-dots">
        {tabsData.map((tab, index) => (
          <button
            key={tab.key}
            type="button"
            className={`giftstore-dot ${activeIndex === index ? "active" : ""}`}
            onClick={() => goToIndex(index)}
            aria-label={t(tab.labelKey || `gifts.tabs.${tab.key}`, { defaultValue: tab.label || tab.key })}
          />
        ))}
      </div>
    </div>
  );
}
